import React, { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { ESTRATEGIA_LABELS } from './EstrategiaPortafolioSelector';

/**
 * WelcomeVentanaBanner — saludo profesional UNA VEZ cuando se abre la
 * ventana oficial de elaboración (8 jul 2026, primeros 14 días).
 *
 * Reglas:
 *  - Sólo renderiza si temporal.bienvenida_ventana_activa === true
 *  - Sólo renderiza si el/la docente NO lo ha cerrado antes (localStorage por user_id)
 *  - Tono: mentor presente, sin marketing
 */
export default function WelcomeVentanaBanner({ temporal }) {
    const { user, profile } = useAuth();
    const [dismissed, setDismissed] = useState(true); // optimistic-hide hasta saber

    const userId = user?.id;
    const storageKey = userId ? `welcome_ventana_dismissed_${userId}` : null;

    useEffect(() => {
        if (!storageKey) return;
        try {
            setDismissed(window.localStorage.getItem(storageKey) === '1');
        } catch (_) {
            setDismissed(false);
        }
    }, [storageKey]);

    if (!temporal?.bienvenida_ventana_activa || dismissed) return null;

    const estrategiaId = profile?.perfil?.estrategia_portafolio;
    const estrategiaLabel = ESTRATEGIA_LABELS[estrategiaId];

    const handleDismiss = () => {
        try {
            if (storageKey) window.localStorage.setItem(storageKey, '1');
        } catch (_) { /* ignorar */ }
        setDismissed(true);
    };

    return (
        <Card
            className="border-emerald-200 bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/40"
            data-testid="welcome-ventana-banner"
        >
            <CardContent className="flex items-start gap-4 p-5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
                    <Sparkles className="h-5 w-5 text-emerald-700" />
                </div>
                <div className="flex-1 space-y-1.5">
                    <p className="font-heading text-sm font-bold text-slate-900">
                        Hoy se abre tu ventana oficial de elaboración del Portafolio.
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        {estrategiaLabel ? (
                            <>
                                Tu estrategia activa es <strong>{estrategiaLabel}</strong>. El sistema te acompaña con orientación
                                temporal, indicadores priorizados y recomendaciones profesionales basadas en evidencia
                                CPEIP/MINEDUC durante las próximas 18 semanas.
                            </>
                        ) : (
                            <>
                                El período oficial corre del 8 de julio al 10 de noviembre 2026. El sistema te acompaña con
                                orientación temporal, indicadores priorizados y recomendaciones profesionales basadas en
                                evidencia CPEIP/MINEDUC. Elige tu estrategia cuando estés listo/a.
                            </>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-emerald-100 hover:text-slate-700"
                    aria-label="Cerrar saludo de apertura de ventana"
                    data-testid="welcome-ventana-dismiss"
                >
                    <X className="h-4 w-4" />
                </button>
            </CardContent>
        </Card>
    );
}
